import type { DadosSobre, ParentescoChave } from "@/modules/visitantes/services/visitantesService";

/**
 * Parentesco entre visitantes cadastrados juntos (migration 0104, tabela `pessoa_parentescos`).
 * Lê-se: "<familiar> É <parentesco> DE <pessoa principal>". É genérico de propósito:
 * o futuro módulo Kids vai reaproveitar a mesma tabela.
 */

export const ROTULO_PARENTESCO: Record<ParentescoChave, string> = {
  conjuge: "Cônjuge",
  filho: "Filho(a)",
  irmao: "Irmão(ã)",
  pai_mae: "Pai ou mãe",
  sobrinho: "Sobrinho(a)",
  neto: "Neto(a)",
  amigo: "Amigo(a)",
  outro: "Outro",
  // Só na ficha (lado inverso).
  tio: "Tio(a)",
  avo: "Avô ou avó",
};

/** Opções do select da etapa 2 (as que o banco aceita). */
export const OPCOES_PARENTESCO = [
  "conjuge",
  "filho",
  "irmao",
  "pai_mae",
  "sobrinho",
  "neto",
  "amigo",
  "outro",
] as const;

export const MAX_FAMILIARES = 10;

export type CategoriaIdade = "crianca" | "adolescente" | "adulto";

/** Criança até 11, adolescente de 12 a 17, adulto de 18 em diante (mesma regra do banco). */
export function categoriaDaIdade(idade: number | null): CategoriaIdade | null {
  if (idade === null) return null;
  if (idade <= 11) return "crianca";
  if (idade < 18) return "adolescente";
  return "adulto";
}

export const ROTULO_CATEGORIA: Record<CategoriaIdade, string> = {
  crianca: "Criança",
  adolescente: "Adolescente",
  adulto: "Adulto",
};

export function rotuloAnos(idade: number): string {
  return idade === 1 ? "1 ano" : `${idade} anos`;
}

export const SOBRE_VAZIO: DadosSobre = {
  igrejaSituacao: "",
  igrejaNome: "",
  cidade: "",
  bairro: "",
  endereco: "",
  preferenciaContato: "",
  pedidoOracao: "",
};

/**
 * Respostas que o familiar herda quando o voluntário marca "Mesmas respostas" (Sim): outra igreja,
 * cidade, bairro, endereço e como prefere ser procurado. O pedido de oração NUNCA é copiado:
 * é pessoal de cada um.
 */
export function respostasEmComum(base: DadosSobre): DadosSobre {
  const visita = base.preferenciaContato === "visita";
  return {
    igrejaSituacao: base.igrejaSituacao,
    igrejaNome: base.igrejaSituacao === "outra" ? base.igrejaNome : "",
    cidade: base.cidade,
    bairro: visita ? base.bairro : "",
    endereco: visita ? base.endereco : "",
    preferenciaContato: base.preferenciaContato,
    pedidoOracao: "",
  };
}

const ROTULO_PREF_CURTO = {
  visita: "visita em casa",
  contato: "mensagem ou ligação",
  nao: "prefere não ser procurado(a)",
} as const;

/** Resumo em uma linha do que o "Sim" vai copiar (vazio = nada respondido). */
export function resumoRespostas(s: DadosSobre): string {
  const partes: string[] = [];
  if (s.igrejaSituacao === "outra")
    partes.push(
      s.igrejaNome.trim() ? `frequenta ${s.igrejaNome.trim()}` : "frequenta outra igreja",
    );
  if (s.igrejaSituacao === "sem") partes.push("não frequenta igreja");
  if (s.cidade.trim()) partes.push(`mora em ${s.cidade.trim()}`);
  if (s.preferenciaContato) partes.push(ROTULO_PREF_CURTO[s.preferenciaContato]);
  return partes.join(" · ");
}

/** Dois conjuntos de respostas são iguais (ignora o pedido de oração, que é sempre de cada um). */
export function mesmasRespostas(a: DadosSobre, b: DadosSobre): boolean {
  const x = respostasEmComum(a);
  const y = respostasEmComum(b);
  return (
    x.igrejaSituacao === y.igrejaSituacao &&
    x.igrejaNome.trim() === y.igrejaNome.trim() &&
    x.cidade.trim() === y.cidade.trim() &&
    x.bairro.trim() === y.bairro.trim() &&
    x.endereco.trim() === y.endereco.trim() &&
    x.preferenciaContato === y.preferenciaContato
  );
}
